import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Building2, CheckCircle2, Handshake, Send, Target, Wallet, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { nome_agenzia: "", zona: "", email: "", telefono: "", note: "" };

const fieldCls =
  "h-12 rounded-xl border-white/15 bg-white/5 text-white placeholder:text-slate-400 text-[15px] focus:bg-white/10 transition-colors duration-200";

export const AgencySection = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/agency-leads`, form);
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Controlla i campi e riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="agenzie" className="relative py-20 sm:py-28 bg-[#0A192F] grain overflow-hidden">
      <div
        className="absolute inset-0 -z-0 opacity-40"
        style={{ background: "radial-gradient(50% 60% at 85% 20%, rgba(16,185,129,0.18) 0%, transparent 60%)" }}
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400 mb-4">
              Per le agenzie immobiliari e i proprietari
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight">
              Smetti di rincorrere i clienti. <span className="text-[#10B981]">Loro hanno già scelto la tua zona.</span>
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed text-[15px] sm:text-base max-w-lg">
              Ogni giorno raccogliamo richieste dettagliate da persone che cercano casa nella tua
              zona, con budget dichiarato, camere, zone e note precise. Niente più telefonate a
              freddo: <strong className="text-white font-semibold">parli solo con chi cerca esattamente ciò che
              hai già in portafoglio.</strong>
            </p>
            <ul className="mt-9 space-y-5">
              {[
                { icon: Target, t: "Match mirati, non liste fredde", d: "Ti segnaliamo solo le richieste compatibili con i tuoi immobili: zona, budget, camere e caratteristiche. Quando sblocchi scatta il Match: parli subito con il richiedente nella chat protetta, e hai anche il suo contatto diretto." },
                { icon: Wallet, t: "Paghi solo ciò che ti serve", d: "Ogni richiesta si sblocca singolarmente a partire da € 4,90: vedi zona, budget e requisiti prima di pagare, e dopo lo sblocco il contatto è tuo per sempre. Nessun abbonamento, nessun vincolo." },
                { icon: Handshake, t: "Parla direttamente col titolare", d: "Niente call center: compila il modulo e ti ricontatta direttamente il titolare per attivare la tua zona e illustrarti condizioni e prezzi." },
              ].map(({ icon: Icon, t, d }) => (
                <li key={t} className="flex gap-4">
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/5 border border-white/10 shrink-0">
                    <Icon className="w-5 h-5 text-[#10B981]" />
                  </span>
                  <div>
                    <p className="font-bold text-white text-[15px]">{t}</p>
                    <p className="text-sm text-slate-400 mt-1 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <a
              href="/richieste"
              data-testid="agency-vetrina-link"
              className="group mt-9 inline-flex items-center gap-2.5 rounded-full border-2 border-[#10B981] text-[#10B981] font-extrabold px-7 py-3.5 text-sm hover:bg-[#10B981] hover:text-[#0A192F] transition-all duration-300"
            >
              Vedi le richieste attive
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-[2rem] bg-white/[0.04] backdrop-blur-xl border border-white/10 p-6 sm:p-9"
          >
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="ok"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-14"
                  data-testid="agency-success-message"
                >
                  <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center mb-5">
                    <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Richiesta ricevuta!</h3>
                  <p className="mt-3 text-slate-400 text-sm leading-relaxed max-w-sm mx-auto">
                    Il titolare ti ricontatterà a breve per attivare la tua zona e illustrarti le
                    condizioni (paghi solo le singole richieste che sblocchi).
                  </p>
                </motion.div>
              ) : (
                <motion.form key="form" exit={{ opacity: 0, scale: 0.98 }} onSubmit={submit} className="space-y-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-5 h-5 text-[#10B981]" />
                    <h3 className="font-display text-xl font-bold text-white">Richiedi l'accesso per la tua agenzia</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="ag-nome" className="text-sm font-semibold text-slate-200">Nome agenzia *</Label>
                      <Input id="ag-nome" data-testid="agency-form-nome" required value={form.nome_agenzia} onChange={(e) => set("nome_agenzia", e.target.value)} placeholder="Es. Immobiliare Salento" className={fieldCls} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ag-zona" className="text-sm font-semibold text-slate-200">Zona di operatività *</Label>
                      <Input id="ag-zona" data-testid="agency-form-zona" required value={form.zona} onChange={(e) => set("zona", e.target.value)} placeholder="Es. Lecce e provincia" className={fieldCls} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="ag-email" className="text-sm font-semibold text-slate-200">Email *</Label>
                      <Input id="ag-email" data-testid="agency-form-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="agenzia@email.it" className={fieldCls} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ag-tel" className="text-sm font-semibold text-slate-200">Telefono *</Label>
                      <Input id="ag-tel" data-testid="agency-form-telefono" required type="tel" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="Es. 0832 000000" className={fieldCls} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ag-note" className="text-sm font-semibold text-slate-200">Note (facoltativo)</Label>
                    <Textarea id="ag-note" data-testid="agency-form-note" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Tipo di immobili trattati, dimensione portafoglio..." className="rounded-xl border-white/15 bg-white/5 text-white placeholder:text-slate-400 text-[15px] min-h-[90px] focus:bg-white/10 transition-colors duration-200" />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="agency-form-submit-button"
                    className="group w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#10B981] text-[#0A192F] font-extrabold text-base px-8 py-4 hover:bg-emerald-400 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
                  >
                    {submitting ? (
                      <span className="w-5 h-5 border-2 border-[#0A192F]/30 border-t-[#0A192F] rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4.5 h-4.5 group-hover:translate-x-1 transition-transform duration-300" />
                        Contatta il titolare
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
